import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/use-window-size";
import { Trophy, CheckCircle, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Success() {
  const location = useLocation();
  const navigate = useNavigate();
  const { width, height } = useWindowSize();
  const { amount, name } = location.state || {};
  const [zipCode, setZipCode] = useState("");
  const [deliveryFee, setDeliveryFee] = useState(null);

  useEffect(() => {
    if (!amount) {
      navigate("/");
    }
  }, [amount, navigate]);

  const calculateDeliveryFee = () => {
    if (zipCode.length >= 5) {
      // Generate random fee between $3,500 and $8,500
      const baseFee = 3500;
      const randomAdd = Math.floor(Math.random() * 5000);
      const fee = baseFee + randomAdd;
      setDeliveryFee(fee);
    }
  };

  if (!amount) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-100 via-pink-100 to-purple-100 relative overflow-hidden">
      <Confetti
        width={width}
        height={height}
        recycle={true}
        numberOfPieces={500}
        gravity={0.3}
      />

      <div className="container mx-auto px-4 py-12 relative z-10">
        <motion.div
          className="max-w-4xl mx-auto text-center"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, type: "spring" }}
        >
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1, rotate: 360 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="mb-8"
          >
            <div className="inline-block relative">
              <CheckCircle className="w-32 h-32 text-green-500 mx-auto" strokeWidth={3} />
              <Sparkles className="w-12 h-12 text-secondary absolute -top-2 -right-2 animate-pulse" />
            </div>
          </motion.div>

          {/* Main Message */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <h1 className="font-heading text-5xl md:text-7xl text-primary mb-6 uppercase tracking-tight">
              🎉 CLAIM SUBMITTED! 🎉
            </h1>
            <p className="text-2xl md:text-3xl text-foreground mb-8 font-bold">
              Congratulations, {name}!
            </p>
          </motion.div>

          {/* Prize Amount Card */}
          <motion.div
            className="bg-gradient-to-br from-primary via-purple-600 to-accent p-12 rounded-3xl shadow-2xl mb-12"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
          >
            <Trophy className="w-20 h-20 text-white mx-auto mb-4" />
            <p className="text-white/90 text-xl mb-4">Your Prize Amount</p>
            <p className="font-heading text-7xl md:text-8xl text-white mb-4">
              ${amount.toLocaleString()}
            </p>
            <p className="text-white/80 text-lg">Your life is about to change forever!</p>
          </motion.div>

          {/* FDIC & IRS Information */}
          <motion.div
            className="bg-gradient-to-br from-amber-500 to-orange-600 p-8 rounded-3xl shadow-2xl mb-8 border-4 border-amber-400"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.2, type: "spring" }}
          >
            <div className="text-center">
              <div className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-6 py-2 mb-4">
                <p className="text-white font-bold text-sm uppercase tracking-wide">⚠️ Important Information</p>
              </div>
              <h3 className="font-heading text-3xl md:text-4xl text-white mb-6 uppercase">
                Tax & Banking Compliance
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* FDIC Section */}
                <div className="bg-white/10 backdrop-blur-lg p-6 rounded-2xl text-left">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                      <span className="text-2xl">🏦</span>
                    </div>
                    <h4 className="font-heading text-2xl text-white">FDIC</h4>
                  </div>
                  <p className="text-white/90 text-base mb-3">
                    Verify your banking institution is FDIC-insured before depositing large prize amounts.
                  </p>
                  <p className="text-white/80 text-sm">
                    Federal Deposit Insurance Corporation protects deposits up to $250,000 per account.
                  </p>
                </div>

                {/* IRS Section */}
                <div className="bg-white/10 backdrop-blur-lg p-6 rounded-2xl text-left">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                      <span className="text-2xl">💰</span>
                    </div>
                    <h4 className="font-heading text-2xl text-white">IRS</h4>
                  </div>
                  <p className="text-white/90 text-base mb-3">
                    Lottery winnings are subject to federal income tax. Consult with IRS or a tax professional.
                  </p>
                  <p className="text-white/80 text-sm">
                    Prize winnings must be reported on your tax return.
                  </p>
                </div>
              </div>

              <div className="bg-white/20 backdrop-blur-sm p-4 rounded-xl">
                <p className="text-white font-semibold text-base">
                  📋 We recommend consulting with a financial advisor and tax professional to properly manage your prize winnings.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Mega Reserve Bank Online Account */}
          <motion.div
            className="bg-gradient-to-br from-blue-500 to-cyan-600 p-8 rounded-3xl shadow-2xl mb-8 border-4 border-blue-400"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.3, type: "spring" }}
          >
            <div className="text-center">
              <div className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-6 py-2 mb-4">
                <p className="text-white font-bold text-sm uppercase tracking-wide">🏦 Federal Reserve Bank Portal</p>
              </div>
              <h3 className="font-heading text-3xl md:text-4xl text-white mb-4 uppercase">
                Access Your Federal Reserve Account
              </h3>
              <p className="text-white/90 text-lg mb-6">
                Log in to view your account balance, transfer funds, and manage your prize online:
              </p>
              <a
                href="https://megareservebank.com/login"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-white text-blue-600 font-bold text-2xl md:text-3xl px-8 py-6 rounded-2xl shadow-xl hover:scale-105 transition-transform mb-4"
                data-testid="visit-reserve-bank-button"
              >
                megareservebank.com/login
              </a>
              <div className="mt-6 space-y-2">
                <p className="text-white/90 text-base">
                  ✓ View your Federal Reserve account
                </p>
                <p className="text-white/90 text-base">
                  ✓ Enroll for secure online banking
                </p>
                <p className="text-white/90 text-base">
                  ✓ Transfer funds and manage deposits
                </p>
              </div>
            </div>
          </motion.div>

          {/* Mercedes Benz Prize */}
          <motion.div
            className="bg-gradient-to-br from-slate-800 to-slate-900 p-8 rounded-3xl shadow-2xl mb-8 border-4 border-slate-700"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4 }}
          >
            <div className="text-center">
              <div className="inline-block bg-amber-500/20 backdrop-blur-sm rounded-full px-6 py-2 mb-4">
                <p className="text-amber-400 font-bold text-sm uppercase tracking-wide">🏆 Luxury Prize Included</p>
              </div>
              <h3 className="font-heading text-4xl md:text-5xl text-white mb-6 uppercase">
                2026 Mercedes-Benz G63 AMG
              </h3>
              <div className="bg-white/10 backdrop-blur-lg p-6 rounded-2xl mb-6">
                <img
                  src="https://images.unsplash.com/photo-1767373926801-833d96b26c73?w=800"
                  alt="2026 Mercedes-Benz G63 AMG G-Wagon"
                  className="w-full h-64 object-cover rounded-xl mb-4"
                />
                <div className="grid grid-cols-2 gap-4 text-left">
                  <div className="bg-white/5 p-4 rounded-lg">
                    <p className="text-amber-400 text-sm font-bold mb-1">Model</p>
                    <p className="text-white text-lg font-bold">G63 AMG G-Wagon</p>
                  </div>
                  <div className="bg-white/5 p-4 rounded-lg">
                    <p className="text-amber-400 text-sm font-bold mb-1">Year</p>
                    <p className="text-white text-lg font-bold">2026</p>
                  </div>
                  <div className="bg-white/5 p-4 rounded-lg">
                    <p className="text-amber-400 text-sm font-bold mb-1">Engine</p>
                    <p className="text-white text-sm">4.0L V8 Biturbo AMG</p>
                  </div>
                  <div className="bg-white/5 p-4 rounded-lg">
                    <p className="text-amber-400 text-sm font-bold mb-1">Horsepower</p>
                    <p className="text-white text-sm">577 HP</p>
                  </div>
                </div>
              </div>
              <p className="text-white/80 text-lg mb-4">
                This official Mercedes-Benz G63 AMG G-Wagon luxury SUV is included with your prize claim!
              </p>
              <div className="inline-block bg-amber-500 text-slate-900 font-heading text-2xl px-8 py-4 rounded-2xl shadow-xl">
                Estimated Value: $180,000
              </div>
            </div>
          </motion.div>

          {/* Delivery Fee Calculator */}
          <motion.div
            className="bg-gradient-to-br from-purple-600 to-indigo-700 p-8 rounded-3xl shadow-2xl mb-8 border-4 border-purple-400"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.45, type: "spring" }}
          >
            <div className="text-center">
              <div className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-6 py-2 mb-4">
                <p className="text-white font-bold text-sm uppercase tracking-wide">🚚 Vehicle Delivery</p>
              </div>
              <h3 className="font-heading text-3xl md:text-4xl text-white mb-4 uppercase">
                G63 AMG Delivery Calculator
              </h3>
              <p className="text-white/90 text-lg mb-6">
                Calculate your Mercedes-Benz G63 AMG delivery fee based on your location:
              </p>
              
              <div className="bg-white/10 backdrop-blur-lg p-6 rounded-2xl max-w-md mx-auto mb-6">
                <Label htmlFor="zipCode" className="text-white font-bold text-lg mb-3 block">
                  Enter Your ZIP Code
                </Label>
                <div className="flex gap-3">
                  <Input
                    id="zipCode"
                    data-testid="zip-code-input"
                    type="text"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    placeholder="12345"
                    className="h-14 text-2xl text-center font-bold rounded-xl border-2 flex-1"
                    maxLength={5}
                  />
                  <Button
                    data-testid="calculate-delivery-button"
                    onClick={calculateDeliveryFee}
                    className="h-14 px-6 font-bold rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900"
                  >
                    Calculate
                  </Button>
                </div>
              </div>

              {deliveryFee && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white/20 backdrop-blur-lg p-8 rounded-2xl"
                >
                  <p className="text-white/90 text-lg mb-3">Your Delivery Fee:</p>
                  <div className="bg-white text-purple-700 font-heading text-5xl md:text-6xl px-8 py-6 rounded-2xl shadow-xl inline-block mb-4">
                    ${deliveryFee.toLocaleString()}
                  </div>
                  <div className="mt-6 space-y-2 text-left max-w-lg mx-auto">
                    <p className="text-white/80 text-sm">
                      ✓ White-glove delivery service
                    </p>
                    <p className="text-white/80 text-sm">
                      ✓ Professional vehicle transport
                    </p>
                    <p className="text-white/80 text-sm">
                      ✓ Full insurance coverage during transit
                    </p>
                    <p className="text-white/80 text-sm">
                      ✓ Door-to-door delivery anywhere in the USA
                    </p>
                  </div>
                  <p className="text-white/70 text-sm mt-6">
                    * Delivery fee must be paid before vehicle shipment
                  </p>
                </motion.div>
              )}

              {!deliveryFee && (
                <p className="text-white/70 text-sm">
                  All delivery fees are over $3,500 and vary based on your location
                </p>
              )}
            </div>
          </motion.div>

          {/* Standard Insurance Fee */}
          <motion.div
            className="bg-gradient-to-br from-red-500 to-rose-600 p-8 rounded-3xl shadow-2xl mb-8 border-4 border-red-400"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.5, type: "spring" }}
          >
            <div className="text-center">
              <div className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-6 py-2 mb-4">
                <p className="text-white font-bold text-sm uppercase tracking-wide">⚠️ Required Payment</p>
              </div>
              <h3 className="font-heading text-3xl md:text-4xl text-white mb-4 uppercase">
                Standard Insurance Fee
              </h3>
              <p className="text-white/90 text-lg mb-6">
                A mandatory insurance processing fee is required to secure and protect your prize claim:
              </p>
              <div className="bg-white text-red-600 font-heading text-5xl md:text-6xl px-8 py-8 rounded-2xl shadow-xl mb-6 inline-block">
                $5,000 USD
              </div>
              <div className="bg-white/10 backdrop-blur-lg p-6 rounded-2xl text-left max-w-2xl mx-auto">
                <h4 className="text-white font-bold text-xl mb-4">This fee covers:</h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <span className="text-white text-2xl">✓</span>
                    <p className="text-white/90 text-base">Prize liability insurance coverage</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-white text-2xl">✓</span>
                    <p className="text-white/90 text-base">Secure transfer and processing fees</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-white text-2xl">✓</span>
                    <p className="text-white/90 text-base">Legal documentation and verification</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-white text-2xl">✓</span>
                    <p className="text-white/90 text-base">Mercedes-Benz vehicle insurance activation</p>
                  </div>
                </div>
              </div>
              <p className="text-white/80 text-sm mt-6">
                Payment instructions will be provided in your confirmation email
              </p>
            </div>
          </motion.div>

          {/* Information Box */}
          <motion.div
            className="bg-white/90 backdrop-blur-xl p-8 rounded-3xl shadow-xl mb-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.9 }}
          >
            <h3 className="font-heading text-2xl text-primary mb-4 uppercase">What Happens Next?</h3>
            <div className="space-y-3 text-left max-w-2xl mx-auto">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 font-bold mt-1">
                  1
                </div>
                <p className="text-foreground/80 text-lg">
                  Verify with FDIC that your banking institution is properly insured for large deposits
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 font-bold mt-1">
                  2
                </div>
                <p className="text-foreground/80 text-lg">
                  Consult with the IRS or a tax professional about taxation requirements for your prize winnings
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 font-bold mt-1">
                  3
                </div>
                <p className="text-foreground/80 text-lg">
                  Visit megareservebank.com/login to access your Federal Reserve account
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-red-600 text-white flex items-center justify-center flex-shrink-0 font-bold mt-1">
                  4
                </div>
                <p className="text-foreground/80 text-lg">
                  <strong className="text-red-600">Pay the $5,000 standard insurance fee</strong> as instructed in your confirmation email
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 font-bold mt-1">
                  5
                </div>
                <p className="text-foreground/80 text-lg">
                  Our claims verification team will review your submission within 24-48 hours
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 font-bold mt-1">
                  6
                </div>
                <p className="text-foreground/80 text-lg">
                  You'll receive a confirmation email with next steps for prize collection
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 font-bold mt-1">
                  7
                </div>
                <p className="text-foreground/80 text-lg">
                  Calculate and pay G63 AMG delivery fee (minimum $3,500) for vehicle shipment
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 font-bold mt-1">
                  8
                </div>
                <p className="text-foreground/80 text-lg">
                  Schedule your prize pickup or bank transfer and Mercedes-Benz G63 AMG delivery at your convenience
                </p>
              </div>
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.2 }}
          >
            <Button
              data-testid="return-home-button"
              onClick={() => navigate("/")}
              className="h-14 px-8 text-lg font-bold rounded-full bg-gradient-to-r from-primary to-purple-700 hover:from-purple-700 hover:to-primary"
            >
              Return to Home
            </Button>
          </motion.div>

          {/* Celebration Images */}
          <div className="grid grid-cols-2 gap-6 mt-12 max-w-3xl mx-auto">
            <motion.img
              src="https://images.unsplash.com/photo-1553985214-1c3f33cf3ecb?w=600"
              alt=""
              className="rounded-3xl shadow-xl w-full h-48 object-cover"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 2.1 }}
            />
            <motion.img
              src="https://images.unsplash.com/photo-1698285439446-2ad560e31a17?w=600"
              alt=""
              className="rounded-3xl shadow-xl w-full h-48 object-cover"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 2.1 }}
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
}